import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, AlertTriangle, HelpCircle, Sparkles } from "lucide-react";
import SaleUnitsTour, { suggestForProduct } from "@/components/SaleUnitsTour";
const TOUR_STORAGE_KEY = "productSaleUnitsTourSeen_v1";
import { formatDOP, formatNum } from "@/lib/format";
import FieldHint from "@/components/FieldHint";
import {
  SearchFilters, PaginationBar, DEFAULT_SEARCH_FILTERS, buildProductsQuery,
} from "@/components/ProductSearchControls";
import CategoryFilter from "@/components/CategoryFilter";
import { useConfirm } from "@/components/ConfirmDialog";
import { suggestPriceFromCost, finalWithItbis, priceFromFinal, classifyMargin, MARGIN_STANDARD, MARGIN_MIN } from "@/lib/pricing";

function MarginHint({ price, cost, cfg }) {
  const p = Number(price), c = Number(cost);
  if (!(c > 0 && p > 0)) return null;
  const markup = (p - c) / c;
  const std = cfg?.standard ?? 0.30;
  const mn = cfg?.min ?? 0.05;
  const status = markup < mn ? "blocked" : markup < std ? "warning" : "ok";
  const pct = (markup * 100).toFixed(1);
  const cls = status === "blocked"
    ? "text-destructive font-bold"
    : status === "warning" ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-success";
  const suffix = status === "blocked"
    ? ` — BLOQUEADO (<${(mn * 100).toFixed(0)}%)`
    : status === "warning" ? ` — bajo (<${(std * 100).toFixed(0)}%). Requerirá contraseña admin al guardar` : "";
  return (
    <p className={`text-[11px] mt-1 ${cls}`} data-testid="product-margin-hint">
      Margen: {pct}%{suffix}
    </p>
  );
}

const empty = {
  sku: "", name: "", description: "", category_id: null, subcategory_id: null, warehouse_id: null,
  price: 0, cost: 0, stock: 0, min_stock: 5, unit: "UND", barcode: "",
  itbis_applies: true, active: true,
  allow_fractional: false, sale_units: [],
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(DEFAULT_SEARCH_FILTERS);
  const [categoryId, setCategoryId] = useState(null);
  const [subcategoryId, setSubcategoryId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const debounceRef = useRef(null);
  const confirm = useConfirm();
  // Iter 41 — tour de unidades alternas
  const [tourOpen, setTourOpen] = useState(false);
  // Lee localStorage justo al momento de abrir cada diálogo (no cachea)
  const shouldShowTour = () => {
    try { return !localStorage.getItem(TOUR_STORAGE_KEY); } catch (e) { return false; }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const qs = buildProductsQuery({
        q: search, filters, page, pageSize,
        extra: { category_id: categoryId, subcategory_id: subcategoryId },
      });
      const { data } = await api.get(`/products?${qs}`);
      setProducts(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogs = async () => {
    const [c, s, w] = await Promise.all([
      api.get("/categories"),
      api.get("/subcategories"),
      api.get("/warehouses"),
    ]);
    setCategories(c.data);
    setSubcategories(s.data);
    setWarehouses(w.data);
  };

  // Catálogos: cargan una sola vez
  useEffect(() => { loadCatalogs(); }, []);

  // Productos: recargan con debounce en cambio de query / filtros / paginación
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadProducts, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [search, filters, page, pageSize, categoryId, subcategoryId]);

  // Cualquier cambio de query/filtros debe volver a página 1
  useEffect(() => { setPage(1); }, [search, filters, pageSize, categoryId, subcategoryId]);

  const load = loadProducts;  // alias para compatibilidad con onSubmit/onDelete

  const openNew = () => {
    setForm({ ...empty, warehouse_id: warehouses.find((w) => w.is_default)?.id || null });
    setEditingId(null);
    setOpen(true);
    if (shouldShowTour()) setTimeout(() => setTourOpen(true), 400);
  };

  const openEdit = (p) => {
    setForm({ ...p, sale_units: p.sale_units || [] });
    setEditingId(p.id);
    setOpen(true);
    if (shouldShowTour()) setTimeout(() => setTourOpen(true), 400);
  };

  // Iter 41 — sugerencia inteligente basada en el nombre del producto
  const suggestion = React.useMemo(() => suggestForProduct(form?.name || ""), [form?.name]);
  const applySuggestion = () => {
    if (!suggestion) return;
    setForm({
      ...form,
      unit: suggestion.base,
      allow_fractional: true,
      sale_units: suggestion.suggested.map((s) => ({
        unit: s.unit, ratio: s.ratio, price: 0, active: true,
      })),
    });
    toast.success(`Aplicada sugerencia para ${suggestion.base} (${suggestion.suggested.length} sub-unidad${suggestion.suggested.length > 1 ? "es" : ""})`);
  };

  const [adminPwModal, setAdminPwModal] = useState({ open: false, pending: null, warnings: [], password: "", loading: false });
  // Iter 43 — política de márgenes configurable desde ajustes
  const [marginCfg, setMarginCfg] = useState({ standard: MARGIN_STANDARD, min: MARGIN_MIN });
  useEffect(() => {
    api.get("/settings/pricing").then(({ data }) => {
      if (data) setMarginCfg({
        standard: (Number(data.margin_standard_pct) || 30) / 100,
        min: (Number(data.margin_min_pct) || 5) / 100,
      });
    }).catch(() => {});
  }, []);

  const submitBody = async (body) => {
    if (editingId) {
      await api.put(`/products/${editingId}`, body);
      toast.success("Producto actualizado");
    } else {
      await api.post("/products", body);
      toast.success("Producto creado");
    }
    setOpen(false);
    load();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanSaleUnits = (form.sale_units || [])
        .filter((u) => u.unit && Number(u.ratio) > 0 && Number(u.price) >= 0)
        .map((u) => ({
          unit: String(u.unit).toUpperCase().trim(),
          ratio: Number(u.ratio),
          price: Number(u.price),
          active: u.active !== false,
        }));
      const body = {
        ...form,
        price: Number(form.price), cost: Number(form.cost),
        stock: Number(form.stock), min_stock: Number(form.min_stock),
        allow_fractional: !!form.allow_fractional,
        sale_units: cleanSaleUnits,
      };

      // Iter 42 — validar márgenes localmente (UX)
      const cost = Number(form.cost) || 0;
      const warnings = [];
      const blocked = [];
      const evalMargin = (label, price, unitCost) => {
        if (unitCost <= 0 || price <= 0) return;
        const markup = (price - unitCost) / unitCost;
        const info = { unit: label, price, cost: unitCost, markup_pct: (markup * 100).toFixed(1) };
        if (markup < marginCfg.min) blocked.push(info);
        else if (markup < marginCfg.standard) warnings.push(info);
      };
      if (cost > 0) {
        evalMargin(form.unit || "UND", Number(form.price), cost);
        cleanSaleUnits.forEach((su) => evalMargin(su.unit, su.price, cost / su.ratio));
      }

      if (blocked.length > 0) {
        toast.error(`Margen mínimo ${(marginCfg.min * 100).toFixed(0)}% no cumplido: ${blocked.map((b) => `${b.unit} (${b.markup_pct}%)`).join(", ")}`);
        return;
      }
      if (warnings.length > 0) {
        setAdminPwModal({ open: true, pending: body, warnings, password: "", loading: false });
        return;
      }

      await submitBody(body);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitWithAdminPw = async () => {
    if (!adminPwModal.password) {
      toast.error("Ingresa la contraseña del administrador");
      return;
    }
    setAdminPwModal((m) => ({ ...m, loading: true }));
    try {
      const body = {
        ...adminPwModal.pending,
        admin_override_password: adminPwModal.password,
      };
      await submitBody(body);
      setAdminPwModal({ open: false, pending: null, warnings: [], password: "", loading: false });
    } catch (err) {
      toast.error(formatApiError(err));
      setAdminPwModal((m) => ({ ...m, loading: false }));
    }
  };

  const onDelete = async (p) => {
    const ok = await confirm({
      title: `¿Eliminar ${p.name}?`,
      description: "Se eliminará el producto del catálogo. Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Productos"
        subtitle="Inventario completo. Gestiona SKUs, precios, costos y existencias."
        testid="products-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-primary hover:bg-primary/90 font-bold" data-testid="new-product-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Nuevo producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar producto" : "Nuevo producto"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                  <FieldHint label="SKU" required hint="Código interno único del producto. Sirve para identificarlo en facturas, compras y reportes." />
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required className="mt-1 font-mono" data-testid="product-sku" />
                </div>
                <div className="col-span-1">
                  <FieldHint label="Código de barras" hint="EAN/UPC del fabricante. Permite escanear el producto desde una pistola lectora en el POS." />
                  <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="mt-1 font-mono" />
                </div>
                <div className="col-span-2">
                  <FieldHint label="Nombre" required hint="Nombre comercial que verá el cliente en la factura." />
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="product-name" />
                </div>
                <div className="col-span-2">
                  <FieldHint label="Descripción" hint="Texto descriptivo opcional (marca, modelo, color, especificaciones)." />
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <FieldHint label="Categoría" hint="Familia del producto. Útil para reportes y para organizar el inventario." />
                  <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v || null, subcategory_id: null })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldHint label="Sub-categoría" hint="Especialización dentro de la categoría. Ej: 'Luminarias' dentro de 'Eléctricos'. Solo aparecen las que pertenecen a la categoría elegida." />
                  <Select
                    value={form.subcategory_id || ""}
                    onValueChange={(v) => setForm({ ...form, subcategory_id: v || null })}
                    disabled={!form.category_id}
                  >
                    <SelectTrigger className="mt-1"><SelectValue placeholder={form.category_id ? "Selecciona" : "Primero elige categoría"} /></SelectTrigger>
                    <SelectContent>
                      {subcategories
                        .filter((s) => s.category_id === form.category_id)
                        .map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldHint label="Almacén" hint="Ubicación física donde se guarda. Si manejas múltiples almacenes, cada producto puede estar en uno específico." />
                  <Select value={form.warehouse_id || ""} onValueChange={(v) => setForm({ ...form, warehouse_id: v || null })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldHint label="Costo (DOP)" hint="Lo que te cuesta a ti adquirir el producto (sin ITBIS). Se actualiza automáticamente con la última compra." />
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cost}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const nextForm = { ...form, cost: raw };
                      // Autosugerir precio de venta sólo al CREAR o si aún no se ha definido price
                      if (!editingId || !Number(form.price)) {
                        const s = suggestPriceFromCost(Number(raw), !!form.itbis_applies);
                        if (s.price > 0) nextForm.price = s.price;
                      }
                      setForm(nextForm);
                    }}
                    className="mt-1 font-mono"
                    data-testid="product-cost"
                  />
                </div>
                <div>
                  <FieldHint
                    label="Precio venta (con ITBIS)"
                    required
                    hint="Precio FINAL que verá el cliente en la factura. El sistema calcula automáticamente el precio sin ITBIS para almacenar. Se recomienda un margen ≥ 30% sobre costo."
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={form.itbis_applies
                      ? finalWithItbis(Number(form.price) || 0, true).toFixed(2)
                      : Number(form.price || 0).toFixed(2)}
                    onChange={(e) => {
                      const finalRaw = Number(e.target.value) || 0;
                      const net = priceFromFinal(finalRaw, !!form.itbis_applies);
                      setForm({ ...form, price: net });
                    }}
                    className="mt-1 font-mono"
                    data-testid="product-price-final"
                  />
                  <MarginHint price={form.price} cost={form.cost} cfg={marginCfg} />
                </div>
                <div>
                  <FieldHint
                    label="Precio sin ITBIS (calculado)"
                    hint="Precio neto que se guarda internamente (usado en reportes DGII y contabilidad). Se calcula automáticamente desde el precio con ITBIS."
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={Number(form.price || 0).toFixed(2)}
                    readOnly
                    className="mt-1 font-mono bg-muted/40"
                    data-testid="product-price"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {form.itbis_applies ? "ITBIS 18% desglosado" : "Sin ITBIS (producto exento)"}
                  </p>
                </div>
                <div>
                  <FieldHint label="Existencia actual" hint="Cantidad disponible ahora mismo en almacén. Cada venta resta y cada compra suma automáticamente." />
                  <Input type="number" step="0.01" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 font-mono" data-testid="product-stock" />
                </div>
                <div>
                  <FieldHint label="Existencia mínima" hint="Umbral para alertar stock bajo. Cuando la existencia caiga a este nivel o menos, aparece en el feed del Dashboard." />
                  <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="mt-1 font-mono" />
                </div>
                <div>
                  <FieldHint label="Unidad" hint="Unidad de medida (UND, MTS, KG, GAL, SAC, etc.)." />
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <Switch checked={form.itbis_applies} onCheckedChange={(v) => setForm({ ...form, itbis_applies: v })} id="itbis" />
                  <FieldHint htmlFor="itbis" label="Aplica ITBIS (18%)" hint="Marca esta opción si el producto está gravado con ITBIS. Algunos productos básicos están exentos." className="cursor-pointer" />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="active" />
                  <FieldHint htmlFor="active" label="Activo" hint="Si está desactivado, no aparece para vender en POS pero queda en el sistema para histórico." />
                </div>

                {/* Iter 40 — venta fraccional y unidades alternas */}
                <div className="col-span-2 border-t border-border pt-3 mt-2">
                  <div className="flex items-center gap-3 mb-3">
                    <Switch
                      checked={!!form.allow_fractional}
                      onCheckedChange={(v) => setForm({ ...form, allow_fractional: v })}
                      id="allow-frac"
                      data-testid="product-allow-fractional"
                    />
                    <FieldHint
                      htmlFor="allow-frac"
                      label="Permitir venta fraccional"
                      hint="Habilita vender fracciones de la unidad base (ej. 0.25 lb de clavos) y necesario cuando se venden unidades alternas."
                    />
                  </div>
                  <div className="flex items-end justify-between mb-2 gap-3">
                    <div className="flex-1">
                      <FieldHint
                        label="Unidades alternas de venta"
                        hint={`Vende el mismo producto en unidades más pequeñas. Ejemplo: 1 saco de cemento tiene 94 lb, entonces ratio=94 y el sistema descuenta 1/94 de saco por cada libra vendida. El costo se prorratea automáticamente (costo por sub-unidad = costo del producto ÷ ratio).`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setTourOpen(true)}
                      data-testid="open-tour-btn"
                      title="Ver guía completa"
                    >
                      <HelpCircle className="w-4 h-4 mr-1" /> Guía
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({
                        ...form,
                        sale_units: [...(form.sale_units || []), { unit: "", ratio: 1, price: 0, active: true }],
                        allow_fractional: true,
                      })}
                      data-testid="add-sale-unit-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Agregar unidad
                    </Button>
                  </div>
                  {suggestion && (form.sale_units || []).length === 0 && (
                    <div className="mb-3 p-3 border border-primary/40 bg-primary/5 rounded-md flex items-start gap-2" data-testid="sale-unit-suggestion">
                      <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <div className="flex-1 text-sm">
                        <div className="font-semibold">
                          Sugerencia para este producto ({suggestion.base}):
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">
                          {suggestion.note} Ratios sugeridos: {suggestion.suggested.map((s) => `${s.unit}=${s.ratio}`).join(", ")}.
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={applySuggestion}
                          className="bg-primary font-bold h-7"
                          data-testid="apply-suggestion-btn"
                        >
                          Aplicar sugerencia
                        </Button>
                      </div>
                    </div>
                  )}
                  {(form.sale_units || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Sin unidades alternas. El producto se vende sólo por su unidad base ({form.unit || "UND"}).
                    </p>
                  )}
                  {(form.sale_units || []).map((u, idx) => {
                    const ratio = Number(u.ratio) || 0;
                    const costPer = form.cost > 0 && ratio > 0 ? Number(form.cost) / ratio : 0;
                    const netPrice = Number(u.price) || 0;
                    const finalPrice = form.itbis_applies ? finalWithItbis(netPrice, true) : netPrice;
                    const markup = costPer > 0 && netPrice > 0 ? (netPrice - costPer) / costPer : 0;
                    const status = costPer > 0 && netPrice > 0
                      ? (markup < marginCfg.min ? "blocked" : markup < marginCfg.standard ? "warning" : "ok")
                      : "unknown";
                    const mColor = status === "blocked" ? "text-destructive"
                      : status === "warning" ? "text-amber-600 dark:text-amber-400" : "text-success";
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2" data-testid={`sale-unit-row-${idx}`}>
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-[10px] uppercase text-muted-foreground font-bold">Unidad</label>}
                          <Input
                            value={u.unit}
                            onChange={(e) => {
                              const arr = [...form.sale_units];
                              arr[idx] = { ...arr[idx], unit: e.target.value.toUpperCase() };
                              setForm({ ...form, sale_units: arr });
                            }}
                            placeholder="LB, PIE, KG"
                            className="h-9 font-mono"
                            data-testid={`sale-unit-${idx}-name`}
                          />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <label className="text-[10px] uppercase text-muted-foreground font-bold">Ratio (por {form.unit || "UND"})</label>}
                          <Input
                            type="number" step="0.01" min="0.01"
                            value={u.ratio}
                            onChange={(e) => {
                              const arr = [...form.sale_units];
                              arr[idx] = { ...arr[idx], ratio: e.target.value };
                              setForm({ ...form, sale_units: arr });
                            }}
                            className="h-9 font-mono"
                            data-testid={`sale-unit-${idx}-ratio`}
                          />
                        </div>
                        <div className="col-span-3">
                          {idx === 0 && <label className="text-[10px] uppercase text-muted-foreground font-bold">Precio con ITBIS</label>}
                          <Input
                            type="number" step="0.01" min="0"
                            value={finalPrice.toFixed(2)}
                            onChange={(e) => {
                              const finalRaw = Number(e.target.value) || 0;
                              const net = priceFromFinal(finalRaw, !!form.itbis_applies);
                              const arr = [...form.sale_units];
                              arr[idx] = { ...arr[idx], price: net };
                              setForm({ ...form, sale_units: arr });
                            }}
                            className="h-9 font-mono"
                            data-testid={`sale-unit-${idx}-price-final`}
                          />
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            Neto: RD${netPrice.toFixed(2)}
                          </div>
                        </div>
                        <div className="col-span-4 text-[11px] font-mono">
                          {idx === 0 && <label className="text-[10px] uppercase text-muted-foreground font-bold">Costo · Margen</label>}
                          <div className="h-9 flex items-center gap-1">
                            {costPer > 0
                              ? (
                                <>
                                  <span>RD${costPer.toFixed(2)}</span>
                                  {netPrice > 0 && (
                                    <span className={`${mColor} font-bold`} data-testid={`sale-unit-${idx}-margin`}>
                                      · {(markup * 100).toFixed(1)}%
                                      {status === "warning" ? " ⚠" : status === "blocked" ? " ✗" : ""}
                                    </span>
                                  )}
                                </>
                              )
                              : <span className="text-muted-foreground">—</span>}
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => setForm({ ...form, sale_units: form.sale_units.filter((_, i) => i !== idx) })}
                            data-testid={`sale-unit-${idx}-remove`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <DialogFooter className="col-span-2 mt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-primary font-bold" data-testid="save-product-btn">Guardar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-col lg:flex-row gap-4">
        <CategoryFilter
          categoryId={categoryId}
          subcategoryId={subcategoryId}
          onChange={({ category_id, subcategory_id }) => {
            setCategoryId(category_id);
            setSubcategoryId(subcategory_id);
          }}
          testidPrefix="products-cat"
        />
        <div className="bg-card border border-border rounded-md flex-1 min-w-0">
        <div className="p-3 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-muted-foreground ml-2" />
            <Input
              placeholder="Buscar productos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 focus-visible:ring-0 h-9"
              data-testid="search-products"
            />
            {loading && <span className="text-xs text-muted-foreground pr-2">Buscando…</span>}
          </div>
          <SearchFilters filters={filters} onChange={setFilters} testidPrefix="products-search" />
        </div>
        <div className="overflow-x-auto max-h-[calc(100vh-22rem)] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="overline border-b border-border sticky top-0 bg-card z-10">
              <tr>
                <th className="text-left p-3">SKU</th>
                <th className="text-left p-3">Nombre</th>
                <th className="text-left p-3">Categoría</th>
                <th className="text-right p-3">Existencia</th>
                <th className="text-right p-3">Precio</th>
                <th className="text-right p-3">Costo</th>
                <th className="text-right p-3 w-28">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const cat = categories.find((c) => c.id === p.category_id)?.name;
                const sub = subcategories.find((s) => s.id === p.subcategory_id)?.name;
                const isLow = p.stock <= p.min_stock;
                return (
                  <tr key={p.id} className="border-b border-border/60 hover:bg-accent" data-testid={`product-row-${p.sku}`}>
                    <td className="p-3 data-cell text-xs">{p.sku}</td>
                    <td className="p-3">
                      <div className="font-semibold">{p.name}</div>
                      {!p.active && <span className="text-xs text-muted-foreground">Inactivo</span>}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {cat || "—"}
                      {sub && <div className="text-[10px] mt-0.5 text-primary/70">↳ {sub}</div>}
                    </td>
                    <td className="p-3 text-right data-cell">
                      <span className={isLow ? "text-destructive font-bold inline-flex items-center gap-1" : ""}>
                        {isLow && <AlertTriangle className="w-3 h-3" />}
                        {formatNum(p.stock)} {p.unit}
                      </span>
                    </td>
                    <td className="p-3 text-right data-cell font-semibold">{formatDOP(p.price)}</td>
                    <td className="p-3 text-right data-cell text-muted-foreground">{formatDOP(p.cost)}</td>
                    <td className="p-3 text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.sku}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => onDelete(p)} data-testid={`delete-${p.sku}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {products.length === 0 && !loading && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{search ? `Sin coincidencias para "${search}"` : "Sin productos. Crea el primero."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="products-pag"
        />
        </div>
      </div>
      <SaleUnitsTour open={tourOpen} onClose={() => setTourOpen(false)} />

      {/* Iter 42 — Modal de confirmación admin para márgenes bajos */}
      <Dialog
        open={adminPwModal.open}
        onOpenChange={(v) => { if (!v) setAdminPwModal({ open: false, pending: null, warnings: [], password: "", loading: false }); }}
      >
        <DialogContent data-testid="admin-margin-modal">
          <DialogHeader>
            <DialogTitle className="text-amber-600 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Margen por debajo del estándar
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>Los siguientes precios tienen un margen menor al 30% recomendado. Se requiere la contraseña de un administrador para autorizar:</p>
            <ul className="bg-muted/40 rounded p-2 text-xs space-y-1">
              {adminPwModal.warnings.map((w, i) => (
                <li key={i}>
                  <span className="font-bold">{w.unit}</span>: precio RD${Number(w.price).toFixed(2)} · costo RD${Number(w.cost).toFixed(2)} · margen <span className="text-amber-600 dark:text-amber-400 font-bold">{w.markup_pct}%</span>
                </li>
              ))}
            </ul>
            <div>
              <label className="text-xs uppercase font-bold text-muted-foreground">Contraseña de administrador</label>
              <Input
                type="password"
                value={adminPwModal.password}
                onChange={(e) => setAdminPwModal((m) => ({ ...m, password: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitWithAdminPw(); } }}
                autoFocus
                className="mt-1"
                data-testid="admin-pw-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Si tú eres administrador, ingresa tu propia contraseña.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminPwModal({ open: false, pending: null, warnings: [], password: "", loading: false })}>
              Cancelar
            </Button>
            <Button
              onClick={submitWithAdminPw}
              disabled={adminPwModal.loading || !adminPwModal.password}
              className="bg-primary font-bold"
              data-testid="admin-pw-confirm"
            >
              {adminPwModal.loading ? "Verificando…" : "Autorizar y guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
